import { Edi214ShipmentStatus } from "../../generated/graphql";

export type Edi214Option = {
  readonly status: Edi214ShipmentStatus;
  readonly label: string;
  /** Sent only by the notification engine, so it waits on that event's cutover. */
  readonly engineOnly: boolean;
};

export type Edi214Group = {
  readonly title: string;
  readonly options: readonly Edi214Option[];
};

/** The helper line shown next to an engine-only status. */
export const ENGINE_ONLY_NOTE =
  "Sent only once notification cutover is on for this event.";

/**
 * Kept in the enum only because Prisma cannot drop an enum value. Nothing
 * sends them, so the page offers them only to untick and the backend refuses
 * them. The backend keeps its own copy in `tenedi-connections.constants.ts`;
 * the two apps share no code.
 */
export const DEPRECATED_214_STATUSES: ReadonlySet<Edi214ShipmentStatus> =
  new Set([
    Edi214ShipmentStatus.LineHaulReceived,
    Edi214ShipmentStatus.LineHaulDeparted,
  ]);

const RETIRED_214_LABELS: Readonly<Record<string, string>> = {
  [Edi214ShipmentStatus.LineHaulReceived]: "Line haul received",
  [Edi214ShipmentStatus.LineHaulDeparted]: "Line haul departed",
};

/** The title of the group that lists retired statuses a partnership still has. */
export const RETIRED_214_GROUP_TITLE = "Retired (untick to save)";

/**
 * The retired statuses already on a partnership, as a group the dialog shows
 * ticked so the operator can untick them. The backend refuses a save that
 * keeps one. Null when the partnership has none, so the dialog never offers a
 * retired status.
 */
export const selectedRetired214Group = (
  selected: readonly Edi214ShipmentStatus[],
): Edi214Group | null => {
  const options = selected
    .filter((status) => DEPRECATED_214_STATUSES.has(status))
    .map((status) => ({
      status,
      label: RETIRED_214_LABELS[status] ?? status,
      engineOnly: false,
    }));
  return options.length === 0
    ? null
    : { title: RETIRED_214_GROUP_TITLE, options };
};

const option = (
  status: Edi214ShipmentStatus,
  label: string,
  engineOnly = false,
): Edi214Option => ({ status, label, engineOnly });

/**
 * The 214 statuses grouped the way an operations person follows a shipment
 * (plan section 2.4). No X12 codes yet: Tiago has not confirmed the AT7 map.
 */
export const EDI_214_GROUPS: readonly Edi214Group[] = [
  {
    title: "Pickup",
    options: [
      option(
        Edi214ShipmentStatus.PickupAppointmentConfirmed,
        "Appointment confirmed",
      ),
      option(
        Edi214ShipmentStatus.PickupAppointmentRescheduled,
        "Appointment rescheduled",
      ),
      option(
        Edi214ShipmentStatus.PickupAppointmentCancelled,
        "Appointment cancelled",
        true,
      ),
      option(Edi214ShipmentStatus.PickupDispatched, "Dispatched"),
      option(Edi214ShipmentStatus.PickupArrived, "Arrived"),
      option(Edi214ShipmentStatus.PickupCompleted, "Picked up"),
      option(Edi214ShipmentStatus.PickupRefused, "Refused", true),
      option(Edi214ShipmentStatus.PickupTransferred, "Transferred"),
      option(Edi214ShipmentStatus.RecoveryCompleted, "Recovery completed"),
    ],
  },
  {
    title: "Line haul",
    options: [
      option(Edi214ShipmentStatus.OrderReceivedAtOrigin, "Received at origin"),
      option(Edi214ShipmentStatus.LineHaulDepartedOrigin, "Departed origin"),
      option(
        Edi214ShipmentStatus.LineHaulArrivedAtDestination,
        "Arrived at destination",
      ),
    ],
  },
  {
    title: "Delivery",
    options: [
      option(
        Edi214ShipmentStatus.DeliveryAppointmentConfirmed,
        "Appointment confirmed",
      ),
      option(
        Edi214ShipmentStatus.DeliveryAppointmentRescheduled,
        "Appointment rescheduled",
      ),
      option(
        Edi214ShipmentStatus.DeliveryAppointmentCancelled,
        "Appointment cancelled",
        true,
      ),
      option(Edi214ShipmentStatus.DeliveryOnHand, "On hand"),
      option(Edi214ShipmentStatus.DeliveryDispatched, "Out for delivery"),
      option(Edi214ShipmentStatus.DeliveryArrived, "Arrived"),
      option(Edi214ShipmentStatus.DeliveryCompleted, "Delivered"),
      option(Edi214ShipmentStatus.DeliveryRefused, "Refused", true),
    ],
  },
  {
    title: "Exceptions",
    options: [
      option(Edi214ShipmentStatus.ContactAttempted, "Contact attempted", true),
    ],
  },
];

const GROUP_BY_STATUS = new Map(
  EDI_214_GROUPS.flatMap((group) =>
    group.options.map((entry) => [entry.status, { group, entry }] as const),
  ),
);

/** "Pickup: Picked up". The status itself when no group lists it. */
export const edi214StatusLabel = (status: Edi214ShipmentStatus): string => {
  const found = GROUP_BY_STATUS.get(status);
  return found === undefined
    ? status
    : `${found.group.title}: ${found.entry.label}`;
};

/**
 * Ticks or unticks one status, keeping the list in the order the groups show
 * it, so the saved list and the change summary read the same way each time.
 */
export const toggleEdi214Status = (
  selected: readonly Edi214ShipmentStatus[],
  status: Edi214ShipmentStatus,
  checked: boolean,
): Edi214ShipmentStatus[] => {
  const next = new Set(selected);
  if (checked) {
    next.add(status);
  } else {
    next.delete(status);
  }
  const ordered = EDI_214_GROUPS.flatMap((group) =>
    group.options.map((entry) => entry.status),
  ).filter((value) => next.has(value));
  // A status no group lists (a retired one already on the row) stays at the
  // end, so the backend's retired-status check reports it by name.
  const unlisted = [...next].filter((value) => !GROUP_BY_STATUS.has(value));
  return [...ordered, ...unlisted];
};
